import {
  DENIED_RESULTS,
  PENDING_LAPSE_MS,
  beginConnect,
  cancel,
  onFlowEvent,
  onResult,
  readConnections,
  settle,
  viewOf,
  type ConnectResultMessage,
  type ConnectionView,
} from '@modules/integrations';

import { offersReconnect } from './connect-content';
import { openConnectCards, useConversationsStore } from './store/conversations.store';
import type { ConnectCardRecord, ConnectPending } from './types';

// The page's one listener for the Connect cards (CC D16). A Connect ends asynchronously, in
// another tab, while another conversation may be active or the panel closed, so the answers come
// from here, by conversation, and never from a mounted card. A card answers only for its own
// pending Connect, which only a click on that card sets: every other Connect only refetches.

/**
 * How far this client's clock may run ahead of the CRM's when a Connect's start on the CRM's
 * clock is unknown (CC D16).
 */
export const CLOCK_SKEW_MS = 5 * 60_000;

interface PendingCard {
  conversationId: string;
  card: ConnectCardRecord & { pending: ConnectPending };
}

function pendingCards(service?: string): PendingCard[] {
  return openConnectCards().flatMap(({ conversationId, card }) =>
    card.pending && (service === undefined || card.pending.service === service)
      ? [{ conversationId, card: { ...card, pending: card.pending } }]
      : [],
  );
}

/**
 * The service reads connected, and its connection works: the search may run. `connected: true`
 * with a status other than `ERROR` (an admin-blocked connection) or `REAUTHORIZATION_REQUIRED`
 * (the CRM keeps `connected` over a lost connection while connectors' live status does not
 * answer). For a Reconnect, `CONNECTING` does not count either: the lapsed connection reads
 * `connected: true` while a Connect runs over it (CC D10, D15).
 */
export function readsConnected(view: ConnectionView | undefined, reconnect = false): boolean {
  return (
    view !== undefined &&
    view.connected &&
    view.status !== 'ERROR' &&
    view.status !== 'REAUTHORIZATION_REQUIRED' &&
    !(reconnect && view.status === 'CONNECTING')
  );
}

/** The service reads connected (as `readsConnected`), its status changed at or after `since`. */
export function connectedAfter(
  view: ConnectionView | undefined,
  since: number | undefined,
  reconnect: boolean,
): boolean {
  if (since === undefined || !readsConnected(view, reconnect) || !view?.statusChangedAt) {
    return false;
  }
  return Date.parse(view.statusChangedAt) >= since;
}

/**
 * The service reads connected since the card's own Connect began: that Connect's `ok`. The start
 * is on the CRM's clock (`serverStartedAt`). Before that is known, a Connect allows for this
 * client's clock running ahead (CLOCK_SKEW_MS), and a Reconnect counts only the `ok` itself: its
 * lapsed connection may read connected from before the click.
 */
export function connectedSince(
  view: ConnectionView | undefined,
  pending: ConnectPending,
  reconnect: boolean,
): boolean {
  const since =
    pending.serverStartedAt ?? (reconnect ? undefined : pending.startedAt - CLOCK_SKEW_MS);
  return connectedAfter(view, since, reconnect);
}

function answer({ conversationId, card }: PendingCard): void {
  void useConversationsStore.getState().answerConnectCard(conversationId, card.proposal);
}

function update(
  { conversationId, card }: PendingCard,
  patch: Partial<Pick<ConnectCardRecord, 'pending' | 'outcome'>>,
): void {
  useConversationsStore
    .getState()
    .updateConnectCard(conversationId, card.proposal.proposalId, patch);
}

/**
 * A pending Connect past its lapse ends as Denied (`expired`), CC D15. Its own Connect can no
 * longer end in `ok` (the CRM's binding lives 600 s), so any result heard from then on belongs to
 * another Connect, which the card must not answer.
 */
function lapsePending(): void {
  const now = Date.now();
  for (const p of pendingCards()) {
    if (now - p.card.pending.startedAt > PENDING_LAPSE_MS) {
      update(p, {
        pending: undefined,
        outcome: { service: p.card.pending.service, kind: 'denied', code: 'expired' },
      });
    }
  }
}

/**
 * A Connect's end, heard on the channel. Lapsed Connects end first, so the result reaches only
 * live ones. A result with no service (the CRM's `expired` for a spent intent, or its `error` when
 * a route failed) is taken by the page's one pending service when there is exactly one. An `ok`
 * answers every card pending on the service once it reads connected (`CONNECTING` included, as
 * after "open sign-in again"); any other result ends those cards' Connects as Denied or Error.
 */
export async function onConnectResult({ service, result }: ConnectResultMessage): Promise<void> {
  lapsePending();
  let target = service;
  if (!target) {
    const services = new Set(pendingCards().map((p) => p.card.pending.service));
    const [only] = services;
    if (services.size !== 1 || only === undefined) return;
    target = only;
  }
  settle(target);
  // No card waits for this service: the open cards' shared read refetches, nothing answers.
  if (pendingCards(target).length === 0) return;
  if (result !== 'ok') {
    const kind = DENIED_RESULTS.includes(result) ? 'denied' : 'error';
    for (const p of pendingCards(target)) {
      update(p, { pending: undefined, outcome: { service: target, kind, code: result } });
    }
    return;
  }
  const view = viewOf(await readConnections(), target);
  if (!view?.connected) return; // still Connecting: a later check answers it
  for (const p of pendingCards(target)) {
    if (readsConnected(view)) {
      answer(p);
    } else {
      update(p, {
        pending: undefined,
        outcome: { service: target, kind: 'error', code: view.reason ?? 'error' },
      });
    }
  }
}

/**
 * The check at install, on the page becoming visible and when a pending card mounts: a pending
 * Connect past its lapse ends as Denied (`expired`), and a service that reads connected since the
 * card's Connect began (`connectedSince`) counts as that Connect's `ok`, so a reload during a
 * sign-in still answers.
 */
export async function checkPendingConnects(): Promise<void> {
  lapsePending();
  if (pendingCards().length === 0) return;
  const views = await readConnections();
  if (!views) return;
  for (const p of pendingCards()) {
    const { service } = p.card.pending;
    if (connectedSince(viewOf(views, service), p.card.pending, offersReconnect(p.card, service))) {
      answer(p);
    }
  }
}

/**
 * The sign-in tab read closed with no result: No answer, its pending Connect kept (CC D16). A
 * lapsed one ends as Denied (`expired`) instead.
 */
function noAnswer(service: string): void {
  lapsePending();
  for (const p of pendingCards(service)) {
    update(p, { outcome: { service, kind: 'no_answer' } });
  }
}

let installed = false;

/** Installs the page's one listener and its checks. Once per page (`initAiCio`). */
export function installConnectAnswers(): void {
  if (installed) return;
  installed = true;
  onResult((message) => {
    void onConnectResult(message);
  });
  onFlowEvent((event) => {
    noAnswer(event.service);
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkPendingConnects();
  });
  void checkPendingConnects();
}

/**
 * Connect, Try again, Reconnect or "open sign-in again" on a card (for a Google service, the
 * disclosure dialog's Continue). Runs inside the click handler: the tab opens before anything
 * is awaited, and only this click makes the card pending. Once `connectIntent` answered, the
 * service's `CONNECTING` read gives the Connect's start on the CRM's clock.
 */
export function connectFromCard(conversationId: string, proposalId: string, service: string): void {
  const store = useConversationsStore.getState();
  const start = beginConnect(service);
  if (start.status === 'blocked') {
    store.updateConnectCard(conversationId, proposalId, {
      pending: undefined,
      outcome: { service, kind: 'popup_blocked' },
    });
    return;
  }
  const startedAt = start.startedAt;
  store.updateConnectCard(conversationId, proposalId, {
    pending: { service, startedAt },
    outcome: undefined,
  });
  // This click's Connect, while it is still the card's pending one.
  const still = () =>
    pendingCards(service).find(
      (p) =>
        p.conversationId === conversationId &&
        p.card.proposal.proposalId === proposalId &&
        p.card.pending.startedAt === startedAt,
    );
  void start.done.then(async (outcome) => {
    if (outcome.status === 'failed') {
      const p = still();
      if (p) {
        update(p, { pending: undefined, outcome: { service, kind: 'error', code: outcome.code } });
      }
      return;
    }
    const view = viewOf(await readConnections(), service);
    const serverStartedAt =
      view?.status === 'CONNECTING' && view.statusChangedAt
        ? Date.parse(view.statusChangedAt)
        : Number.NaN;
    const p = still();
    if (p && Number.isFinite(serverStartedAt)) {
      update(p, { pending: { ...p.card.pending, serverStartedAt } });
    }
  });
}

/**
 * Cancel on a card: `cancelConnect`, and the card's pending Connect goes (Denied). A consent the
 * user still finishes connects, and the card then shows Connected elsewhere and waits for Continue.
 */
export function cancelFromCard(conversationId: string, proposalId: string, service: string): void {
  void cancel(service);
  useConversationsStore.getState().updateConnectCard(conversationId, proposalId, {
    pending: undefined,
    outcome: { service, kind: 'denied', code: 'cancelled' },
  });
}
